import Accordion from '../../common/accordion/accordion.tsx';
import './driver_accordion.css';
import { ChevronIcon } from '../../../vectors/chevron_icon.tsx';
import { NavLink, useNavigate } from 'react-router';
import { DownloadIcon, GithubIcon, InfoIcon } from '../../../assets/icons.tsx';
import {
    SensorDriverPackage,
    ReleaseState,
    Driver,
} from '../../../types/driver_types.ts';
import { ReleaseStatePill } from '../../common/release_state_pill/release_state_pill.tsx';
import { formatDate, getLatestRelease } from '../../../utils/utils.ts';
import { useMemo } from 'react';
import { MOBILE_BREAKPOINT_WIDTH } from '../../../config/constants.ts';
import { useAzureAuth } from '../../../hooks/use_azure_auth.ts';

interface DriverAccordionProps {
    open?: boolean;
    driverPackage: SensorDriverPackage;
}

const DriverAccordion = ({ open, driverPackage }: DriverAccordionProps) => {
    const navigate = useNavigate();
    const {
        userProps: { token: token },
    } = useAzureAuth();
    const showReleaseState = Boolean(token);

    // Group drivers by platform
    const groupedDrivers = driverPackage.drivers.reduce(
        (acc, driver) => {
            const targetKey = driver.sw_target.name;
            if (!acc[targetKey]) {
                acc[targetKey] = [];
            }
            acc[targetKey].push(driver);
            return acc;
        },
        {} as Record<string, Driver[]>,
    );

    // Cache to reduce repeated calls to getLatestRelease
    const latestReleases = useMemo(() => {
        const releases = new Map();
        driverPackage.drivers.forEach(driver => {
            releases.set(driver.id, getLatestRelease(driver));
        });
        return releases;
    }, [driverPackage.drivers]);

    let releaseState: ReleaseState = 'PUB';
    const releaseStateSet = new Set(
        driverPackage.drivers.map(driver => driver.release_state),
    );
    if (releaseStateSet.size == 1) {
        releaseState = releaseStateSet.values().next().value as ReleaseState;
    } else if (releaseStateSet.size > 1) {
        releaseState = 'MIXED';
    }

    return (
        <Accordion open={open}>
            <Accordion.Header>
                <div className="driver_accordion__header">
                    <div>{driverPackage.sensorName}</div>
                    <div>{driverPackage.drivers.length}</div>
                    <div>
                        {showReleaseState && (
                            <ReleaseStatePill releaseState={releaseState} />
                        )}
                    </div>
                    <ChevronIcon
                        className="driver_accordion__header__icon"
                        aria-hidden
                    />
                </div>
            </Accordion.Header>
            <Accordion.Content>
                <div className="driver_accordion__content">
                    <table className="driver_info_table">
                        <thead>
                            <tr>
                                <th>Platform</th>
                                <th className="driver_info_table__interface_header">
                                    Interface
                                </th>
                                <th>Latest Release</th>
                                <th className="driver_info_table__date_header">
                                    Release Date
                                </th>
                                <th className="driver_info_table__release_state_header"></th>
                                <th className="driver_info_table__actions_header"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(groupedDrivers).map(
                                ([platform, drivers], index) => {
                                    const driverId = drivers[0]?.id;
                                    return (
                                        <tr
                                            key={index}
                                            className="driver_info_table__row"
                                            onClick={() => {
                                                if (
                                                    window.innerWidth <=
                                                    MOBILE_BREAKPOINT_WIDTH
                                                ) {
                                                    navigate(
                                                        `/driver/${driverId}`,
                                                    );
                                                }
                                            }}
                                        >
                                            <th>
                                                <div>{platform}</div>
                                                <div className="driver_info_table__mobile_sub_info driver_info_table__subrow_container">
                                                    {drivers.map(
                                                        (driver, idx) => (
                                                            <div key={idx}>
                                                                {
                                                                    driver
                                                                        .interface
                                                                        .name
                                                                }
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </th>
                                            <td className="driver_info_table__interface_data">
                                                <div className="driver_info_table__subrow_container">
                                                    {drivers.map(
                                                        (driver, idx) => (
                                                            <div key={idx}>
                                                                {
                                                                    driver
                                                                        .interface
                                                                        .name
                                                                }
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="driver_info_table__subrow_container">
                                                    {drivers.map((d, idx) => (
                                                        <div key={idx}>
                                                            <div>
                                                                {latestReleases.get(
                                                                    d.id,
                                                                )?.version ??
                                                                    '-'}
                                                            </div>
                                                            <div className="driver_info_table__mobile_sub_info">
                                                                {latestReleases.get(
                                                                    d.id,
                                                                )
                                                                    ? formatDate(
                                                                          latestReleases.get(
                                                                              d.id,
                                                                          )!
                                                                              .release_date,
                                                                      )
                                                                    : '-'}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="driver_info_table__date_data">
                                                <div className="driver_info_table__subrow_container">
                                                    {drivers.map((d, idx) => (
                                                        <div key={idx}>
                                                            {latestReleases.get(
                                                                d.id,
                                                            )
                                                                ? formatDate(
                                                                      latestReleases.get(
                                                                          d.id,
                                                                      )!
                                                                          .release_date,
                                                                  )
                                                                : '-'}
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                            {showReleaseState && (
                                                <td className="driver_info_table__release_state_data">
                                                    <div className="driver_info_table__subrow_container">
                                                        {drivers.map(
                                                            (driver, idx) => (
                                                                <div key={idx}>
                                                                    <ReleaseStatePill
                                                                        releaseState={
                                                                            driver.release_state as ReleaseState
                                                                        }
                                                                    />
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                            <td className="driver_info_table__actions_data">
                                                <div>
                                                    {drivers.map(
                                                        (driver, idx) => (
                                                            <div
                                                                key={idx}
                                                                className="driver_table_cell_actions"
                                                            >
                                                                <a
                                                                    className="driver_info_table__link_button driver_info_table__link_button_enabled"
                                                                    href={
                                                                        driver.repository_link
                                                                    }
                                                                    title="go to repository"
                                                                >
                                                                    <GithubIcon />
                                                                </a>
                                                                {latestReleases.get(
                                                                    driver.id,
                                                                ) ? (
                                                                    <a
                                                                        className="driver_info_table__link_button driver_info_table__link_button_enabled"
                                                                        href={
                                                                            latestReleases.get(
                                                                                driver.id,
                                                                            )!
                                                                                .download_link
                                                                        }
                                                                        title="get latest release as .zip"
                                                                    >
                                                                        <DownloadIcon />
                                                                    </a>
                                                                ) : (
                                                                    <div
                                                                        className="driver_info_table__link_button driver_info_table__link_button_disabled"
                                                                        title="No release available for download"
                                                                    >
                                                                        <DownloadIcon />
                                                                    </div>
                                                                )}

                                                                <NavLink
                                                                    className="driver_info_table__link_button driver_info_table__link_button_enabled"
                                                                    to={`/driver/${driver.id}`}
                                                                    title="driver information page"
                                                                >
                                                                    <InfoIcon />
                                                                </NavLink>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                },
                            )}
                        </tbody>
                    </table>
                </div>
            </Accordion.Content>
        </Accordion>
    );
};

export default DriverAccordion;
